/**
 * API客户端 - 统一的业务数据访问层
 * 负责解包后端响应、类型收敛、错误规范化
 *
 * 设计原则：
 * - 后端保持 ApiResponse<T> 格式（便于网关/审计/兼容）
 * - 前端业务代码只接触干净的业务类型 T
 * - 在这一层完成解包、类型转换、错误处理
 * - 拦截器保持无侵入，只在SDK层做解包
 */

import { AxiosResponse } from 'axios';

// ============= 类型定义 =============

/**
 * 后端标准响应格式（传输层）
 */
export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
  traceId?: string;
  timestamp?: number;
}

/**
 * 自定义API错误类
 */
export class ApiError extends Error {
  constructor(
    public code: number,           // 业务码（非0即业务错误；-1/-2为客户端自定义）
    message: string,
    public traceId?: string,
    public originalError?: any,
    public httpStatus?: number     // HTTP状态码
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 业务结果类型（业务层）
 */
export type Result<T> = {
  success: true;
  data: T;
} | {
  success: false;
  error: ApiError;
};

/**
 * API调用选项
 */
export interface ApiCallOptions<T = unknown> {
  raw?: boolean;           // 跳过解包，返回原始响应
  passthrough?: boolean;   // 直通模式，用于已经是业务数据的接口
  schema?: {              // 可选的运行时校验，自动推导类型
    parse: (value: unknown) => T;
  };

  // 缓存选项
  cache?: {
    key: string;        // 缓存键（建议基于 URL+params 生成）
    ttl: number;        // 毫秒
    persist?: boolean;  // 是否写 localStorage/WebStore
    swr?: boolean;      // 过期后是否先回旧值再后台刷新
    dedupe?: boolean;   // 并发去重（默认 true）
  };
}

/**
 * 缓存条目类型
 */
type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

/**
 * API客户端配置
 */
export interface ApiClientConfig {
  showErrorMessage?: boolean;
  logErrors?: boolean;
  onError?: (error: ApiError) => void;  // 错误回调，解耦UI依赖
}

// ============= 工具函数 =============

/**
 * 构建缓存键，避免参数顺序导致的键冲突
 */
export function buildCacheKey(url: string, params?: Record<string, any>): string {
  if (!params) return url;
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  return q ? `${url}?${q}` : url;
}

/**
 * 类型守卫：检查是否为标准API响应格式
 * 放宽包裹类型守卫，允许错误分支没有 data 字段
 */
function isApiResponse(data: any): data is Omit<ApiResponse<any>, 'data'> & { data?: any } {
  return data &&
         typeof data === 'object' &&
         typeof data.code === 'number' &&
         typeof data.message === 'string';
}

/**
 * 类型守卫：检查是否为空响应（204等）
 */
function isEmptyResponse(response: AxiosResponse): boolean {
  return response.status === 204 ||
         response.data === null ||
         response.data === undefined ||
         response.data === '';
}

/**
 * 类型守卫：检查是否为二进制响应
 * 使用 response.config.responseType 更稳定
 */
function isBinaryResponse(response: AxiosResponse): boolean {
  const contentType = response.headers?.['content-type'] as string | undefined;
  const responseType = (response.config as any)?.responseType; // 'blob' | 'arraybuffer' | ...
  return responseType === 'blob' ||
         responseType === 'arraybuffer' ||
         Boolean(contentType && /octet-stream|application\/pdf|image\/|audio\/|video\//i.test(contentType));
}

// ============= API客户端类 =============

export class ApiClient {
  private config: ApiClientConfig;
  private memCache = new Map<string, CacheEntry<unknown>>();
  private inflight = new Map<string, Promise<any>>();

  constructor(config: ApiClientConfig = {}) {
    this.config = {
      showErrorMessage: true,
      logErrors: true,
      ...config
    };
  }

  /**
   * 从持久化存储获取缓存
   */
  private getPersist(key: string): any {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * 写入持久化存储
   */
  private setPersist(key: string, val: any): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(val));
      }
    } catch {}
  }

  /**
   * 读取缓存（内存优先，然后持久化）
   */
  private readCache<T>(key: string): CacheEntry<T> | null {
    const hit = this.memCache.get(key) as CacheEntry<T> | undefined;
    if (hit) return hit;
    const persisted = this.getPersist(`api:cache:${key}`);
    return persisted ?? null;
  }

  /**
   * 写入缓存（内存 + 可选持久化）
   */
  private writeCache<T>(key: string, value: T, ttl: number, persist?: boolean): void {
    const entry: CacheEntry<T> = { value, expiresAt: Date.now() + ttl };
    this.memCache.set(key, entry);
    if (persist) {
      this.setPersist(`api:cache:${key}`, entry);
    }
  }

  /**
   * 失效指定缓存
   */
  invalidate(key: string): void {
    this.memCache.delete(key);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(`api:cache:${key}`);
      }
    } catch {}
  }

  /**
   * 按前缀批量失效缓存
   */
  invalidateByPrefix(prefix: string): void {
    for (const k of this.memCache.keys()) {
      if (k.startsWith(prefix)) {
        this.memCache.delete(k);
      }
    }
    try {
      if (typeof localStorage !== 'undefined') {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith(`api:cache:${prefix}`)) {
            localStorage.removeItem(k);
          }
        }
      }
    } catch {}
  }

  /**
   * 解包并转换响应数据
   * 将 AxiosResponse<ApiResponse<T>> 转换为 T
   */
  private unwrapResponse<T>(
    response: AxiosResponse,
    options: ApiCallOptions<T> = {}
  ): T {
    // 原始模式：直接返回响应
    if (options.raw) {
      return response as unknown as T;
    }

    // 二进制文件自动旁路（更顺滑）
    if (isBinaryResponse(response) && !options.passthrough) {
      return response.data as unknown as T;
    }

    // 空响应处理，返回 undefined 比 null 更贴近 TS 语义
    if (isEmptyResponse(response)) {
      return undefined as unknown as T;
    }

    const responseData = response.data;

    // 直通模式：响应数据已经是业务数据
    if (options.passthrough) {
      return this.validateAndReturn(responseData, options);
    }

    // 标准API响应格式处理
    if (!isApiResponse(responseData)) {
      throw new ApiError(
        -1,
        'Invalid API response format',
        undefined,
        responseData
      );
    }

    const { code, message, data, traceId } = responseData;

    // 从响应头兜底 traceId
    const headerTrace = response.headers?.['x-trace-id'] ?? response.headers?.['trace-id'];
    const finalTraceId = traceId ?? headerTrace;

    // 只认 code === 0 为成功
    if (code !== 0) {
      throw new ApiError(code, message || '请求失败', finalTraceId, responseData);
    }

    // 成功但后端未给 data 时，按约定返回 undefined
    return this.validateAndReturn(data ?? undefined, options);
  }

  /**
   * 验证并返回数据
   */
  private validateAndReturn<T>(data: any, options: ApiCallOptions<T> = {}): T {
    // 可选的运行时校验
    if (options.schema) {
      try {
        return options.schema.parse(data);
      } catch (error) {
        throw new ApiError(
          -2,
          `数据校验失败: ${error instanceof Error ? error.message : '未知错误'}`,
          undefined,
          error
        );
      }
    }

    return data as T;
  }

  /**
   * 处理错误
   */
  private handleError(error: any): never {
    let apiError: ApiError;

    if (error instanceof ApiError) {
      // 已经是API错误
      apiError = error;
    } else if (error.response) {
      // HTTP错误 - 区分业务码与HTTP状态码
      const { status, data } = error.response;
      const message = (data && typeof data === 'object' && data.message)
        ? data.message
        : `HTTP ${status} 错误`;
      const traceId = data?.traceId ?? error.response.headers?.['x-trace-id'] ?? error.response.headers?.['trace-id'];
      // 使用 -2 作为HTTP错误的业务码，保留原始HTTP状态码
      apiError = new ApiError(-2, message, traceId, error, status);
    } else {
      // 网络错误或其他错误
      apiError = new ApiError(
        -1,
        error.message || '网络请求失败',
        undefined,
        error
      );
    }

    // 错误回调处理（解耦UI依赖）
    if (this.config.onError) {
      this.config.onError(apiError);
    }
    // 注意：不在这里显示错误消息和记录日志，因为 ErrorManager 已经统一处理了
    // 避免重复打印到控制台
    throw apiError;
  }

  /**
   * 执行API调用并解包数据
   */
  async call<T>(
    apiCall: () => Promise<AxiosResponse>,
    options: ApiCallOptions<T> = {}
  ): Promise<T> {
    const c = options.cache;

    // 实际请求执行函数
    const doRequest = async (): Promise<T> => {
      const resp = await apiCall();
      const data = this.unwrapResponse<T>(resp, options);
      if (c) {
        this.writeCache<T>(c.key, data, c.ttl, c.persist);
      }
      return data;
    };

    // 无缓存配置 → 走原流程
    if (!c) {
      try {
        return await doRequest();
      } catch (e) {
        return this.handleError(e);
      }
    }

    const now = Date.now();
    const cached = this.readCache<T>(c.key);
    const fresh = cached && cached.expiresAt > now;
    const expired = cached && cached.expiresAt <= now;

    // 并发去重（默认开启）
    const dedupe = c.dedupe !== false;

    if (fresh) {
      // 命中新鲜缓存
      return cached!.value;
    }

    if (expired && c.swr) {
      // SWR：先回陈旧值，再后台刷新（不打断调用方）
      if (dedupe && this.inflight.has(c.key)) {
        // 已有后台刷新在进行，直接回旧值
        return cached!.value;
      }
      const p = doRequest()
        .finally(() => this.inflight.delete(c.key))
        .catch(() => {}); // 静默刷新失败
      if (dedupe) this.inflight.set(c.key, p);
      return cached!.value; // 立即返回旧值
    }

    // 没有缓存或缓存过期但不走 SWR → 发请求
    if (dedupe) {
      const inflight = this.inflight.get(c.key);
      if (inflight) return inflight as Promise<T>;
      const p = doRequest().finally(() => this.inflight.delete(c.key));
      this.inflight.set(c.key, p);
      try {
        return await p;
      } catch (e) {
        return this.handleError(e);
      }
    } else {
      try {
        return await doRequest();
      } catch (e) {
        return this.handleError(e);
      }
    }
  }

  /**
   * 执行API调用并返回Result类型
   * 复用 call() 方法，保持缓存/去重/SWR行为一致
   */
  async callSafe<T>(
    apiCall: () => Promise<AxiosResponse>,
    options: ApiCallOptions<T> = {}
  ): Promise<Result<T>> {
    try {
      // 复用 call()，继承缓存 / 去重 / SWR
      const data = await this.call<T>(apiCall, options);
      return { success: true, data };
    } catch (error: any) {
      const apiError = error instanceof ApiError
        ? error
        : new ApiError(-1, error.message || '请求失败', undefined, error);

      return {
        success: false,
        error: apiError
      };
    }
  }
}

// ============= 默认实例 =============

/**
 * 默认API客户端实例
 * 注意：禁用 logErrors，避免与 http.ts 的 errorManager 重复打印日志
 */
export const apiClient = new ApiClient({
  logErrors: false  // 错误日志由 http.ts 的 errorManager 统一处理
});

/**
 * 静默API客户端实例（不显示错误消息）
 */
export const silentApiClient = new ApiClient({
  showErrorMessage: false
});

/**
 * 原始API客户端实例（用于文件下载等场景）
 */
export const rawApiClient = new ApiClient({
  showErrorMessage: false,
  logErrors: false
});

// ============= 便捷方法 =============

/**
 * 快捷调用方法 - 自动解包数据（常规模式）
 */
export function unwrap<T>(
  apiCall: () => Promise<AxiosResponse>,
  options?: ApiCallOptions<T>
): Promise<T>;

/**
 * 快捷调用方法 - 原始响应模式
 */
export function unwrap<T>(
  apiCall: () => Promise<AxiosResponse<T>>,
  options: ApiCallOptions<T> & { raw: true }
): Promise<AxiosResponse<T>>;

/**
 * 快捷调用方法 - 实现
 */
export function unwrap<T>(
  apiCall: () => Promise<AxiosResponse>,
  options?: ApiCallOptions<T>
) {
  return apiClient.call<T>(apiCall, options);
}

/**
 * 安全调用方法 - 返回Result类型
 */
export const safecall = <T>(
  apiCall: () => Promise<AxiosResponse>,
  options?: ApiCallOptions<T>
) => {
  return apiClient.callSafe<T>(apiCall, options);
};

/**
 * 原始调用方法 - 显式返回 AxiosResponse<T>
 */
export const rawcall = <T>(apiCall: () => Promise<AxiosResponse<T>>) => {
  return rawApiClient.call<AxiosResponse<T>>(apiCall, { raw: true });
};

/**
 * 直通调用方法 - 用于已经是业务数据的接口
 * 直通接口仍可做 schema 校验
 */
export const passthrough = <T>(
  apiCall: () => Promise<AxiosResponse>,
  options?: Omit<ApiCallOptions<T>, 'passthrough'>
) => {
  return apiClient.call<T>(apiCall, { ...options, passthrough: true });
};

// ============= 缓存管理方法 =============

/**
 * 失效指定缓存
 * @param key 缓存键
 */
export const invalidateCache = (key: string): void => {
  apiClient.invalidate(key);
};

/**
 * 按前缀批量失效缓存
 * @param prefix 缓存键前缀
 */
export const invalidateCacheByPrefix = (prefix: string): void => {
  apiClient.invalidateByPrefix(prefix);
};

/**
 * 刷新主播数据缓存
 * 清除所有 voices 相关的缓存
 */
export const refreshVoiceCache = (): void => {
  invalidateCacheByPrefix('voices:');
  invalidateCache('/api/v1/voices');
  invalidateCache('/api/v1/voices/by_tag');
};
